import { useAuth } from '@agentnet/auth';
import { Box, FormControl, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenReporteDateAPI } from 'api/fincen-report-api';
import { format as formatDate } from 'date-fns';
import { FilingInformationProps, FincenUpdateResponse } from 'features/files/fincen-reporting/types';
import { usePrivacyNoticeQuery } from 'features/help/queries/useFincenContentfulQuery';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AccordionContent from 'ui-kit/components/accordion/AccordionContent';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { AsyncStatus } from '../../constants';
import { PRIVACY_NOTICE_ENTRY_ID } from '../constants';

// Custom rendering options for Contentful rich text
const options = {
  renderMark: {
    [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: ReactNode) => <Typography variant="body2">{children}</Typography>,
  },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lastNotice: {
      marginBottom: 0,
      paddingBottom: 0,
    },
    container: {
      width: '100%',
      marginBottom: theme.spacing(4),
      padding: theme.spacing(0, 3),
    },
    dateField: {
      width: '100%',
    },
    privacyNotice: {
      padding: theme.spacing(1, 1, 1, 1),
      minHeight: 350,
      '& .MuiTypography-paragraph': {
        marginBottom: theme.spacing(3),
      },
      '& .MuiTypography-body1': {
        display: 'block',
        marginBottom: theme.spacing(3),
      },
      '& p': {
        marginBottom: theme.spacing(3),
      },
      '& p:last-child': {
        marginBottom: 0,
      },
    },
    legalNoticeAccordion: {
      width: '100%',
    },
    accordion: {
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(2),
      width: '100%',
    },
    dateFieldContainer: {
      marginBottom: theme.spacing(2),
      width: '100%',
    },
    dateInputContainer: {
      width: '100%',
      maxWidth: 260,
    },
  }),
);

const FilingInformation: React.FC<FilingInformationProps> = ({ onDataLoaded, onFormSubmitted, submitted }) => {
  const { addSnackbarMessage } = useSnackBars();
  const classes = useStyles();
  const [privacyNoticeExpanded, setPrivacyNoticeExpanded] = useState<boolean>(true);
  const [privacyNoticeContent, setPrivacyNoticeContent] = useState<any>(null);
  const [privacyNoticeLoading, setPrivacyNoticeLoading] = useState<boolean>(true);

  const [dateValue, setDateValue] = useState<string | null>(formatDate(new Date(), 'MM/dd/yyyy'));
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fileId } = useParams<GetFileInfoProps>();
  const { getAccessToken } = useAuth();
  const { fincenReport, fincenReportStatus, loadFincenReport, currentFileId } = fincenReportCtx ?? {};
  const fileDataCtx = useContext<FileDataContextInterface | null>(FileDataContext);
  const { fileData } = fileDataCtx ?? {};

  // Fetch privacy notice content from Contentful
  useEffect(() => {
    const fetchPrivacyNotice = async () => {
      try {
        setPrivacyNoticeLoading(true);
        const result = await usePrivacyNoticeQuery(PRIVACY_NOTICE_ENTRY_ID);
        if (!result.error && result.data?.messageText) {
          setPrivacyNoticeContent(result.data.messageText.json);
        }
      } catch (error) {
        console.error('Error fetching privacy notice from Contentful:', error);
      } finally {
        setPrivacyNoticeLoading(false);
      }
    };

    fetchPrivacyNotice();
  }, []);

  useEffect(() => {
    if (fileId && (!fincenReport || fincenReportStatus === 'idle' || currentFileId !== fileId)) {
      loadFincenReport && loadFincenReport(fileId ?? '').catch(() => void 0);
    }
  }, [fileId, fincenReport, fincenReportStatus, currentFileId, loadFincenReport]);

  useEffect(() => {
    if (!fincenReport) return;
    if (onDataLoaded) {
      onDataLoaded(fincenReport);
    }
    if (fincenReport.filingInformation?.datePrepared) {
      setDateValue(fincenReport.filingInformation?.datePrepared);
    }
  }, [fincenReport?.filingInformation?.datePrepared, onDataLoaded]);

  const updateFincenReportDate = async (): Promise<FincenUpdateResponse> => {
    const token = await getAccessToken();
    if (!fincenReport?.filingInformation) {
      throw new Error('Filing information is required');
    }

    const filingInformation = {
      ...fincenReport.filingInformation,
      datePrepared: dateValue || undefined,
    };

    return updateFincenReporteDateAPI(String(fincenReport?.fincenReportId || 0), filingInformation, token);
  };

  const {
    execute: executeUpdateFincenReportDate,
    value: updateFincenReportDateResponse,
    status: updateFincenReportDateStatus,
    errors: updateFincenReportDateErrors,
  } = useAsync<FincenUpdateResponse>(updateFincenReportDate, false);

  useEffect(() => {
    if (submitted) {
      executeUpdateFincenReportDate();
      onFormSubmitted && onFormSubmitted();
    }
  }, [submitted]);

  useEffect(() => {
    if (updateFincenReportDateStatus === AsyncStatus.SUCCESS) {
      addSnackbarMessage({
        message: 'Saving Successful',
        type: 'success',
      });
      fincenReportCtx?.reloadFincenReport?.(fileData?.fileId ?? '0');
    }
  }, [updateFincenReportDateStatus, addSnackbarMessage]);

  const handlePrivacyNoticeExpand = (expanded: boolean) => {
    setPrivacyNoticeExpanded(expanded);
  };

  const handleDateChange = (date: string | null) => {
    setDateValue(date);
  };
  return (
    <div className={classes.container}>
      <LoadingSpinner
        status={fincenReportStatus === AsyncStatus.PENDING ? AsyncStatus.PENDING : AsyncStatus.SUCCESS}
        variant="linear"
      />
      <Box mt={3} mb={2}>
        <Grid container>
          <Grid item xs={12}>
            <FormControl component="fieldset" className={classes.dateFieldContainer}>
              <div className={classes.dateInputContainer}>
                <DateFieldString
                  label="Date Prepared (auto-filled)"
                  required
                  onChange={handleDateChange}
                  value={dateValue}
                  format="MM/dd/yyyy"
                  className={classes.dateField}
                  qaAttribute="fincen-filing-date-prepared-input"
                  disabled={true}
                  iconColor="rgba(0, 0, 0, 0.38)"
                  KeyboardDatePickerProps={{
                    disableToolbar: true,
                    variant: 'inline',
                    maxDate: new Date(),
                    placeholder: 'mm/dd/yyyy',
                    fullWidth: true,
                  }}
                />
              </div>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box mb={2}>
        <div className={classes.legalNoticeAccordion}>
          <AccordionContent
            id="privacy-notice"
            title="Privacy Act and Paperwork Reduction Act Notice"
            hideSubtitle={true}
            expanded={privacyNoticeExpanded}
            onExpandChange={handlePrivacyNoticeExpand}
            data-testid="fincen-filing-privacy-notice-accordion"
            accordianQAAttribute="fincen-filing-privacy-notice-accordion"
            className={classes.accordion}
          >
            <div className={classes.privacyNotice}>
              {privacyNoticeContent && documentToReactComponents(privacyNoticeContent, options)}
            </div>
          </AccordionContent>
        </div>
      </Box>
    </div>
  );
};

export default FilingInformation;
